/**
 * Likes, kept on the profile. A like is the person's bookmark — the profile lists what you
 * liked and takes you back to it — and never a vote: nothing here ranks (docs/RANKING.md).
 * Anonymous actors can like too; the actor survives the upgrade to an account, so the list
 * does. Without a backend a like is this tab's alone.
 */
import type { GameMeta } from '../lib/protocol';
import { ensureSession, supabase } from '../backend/supabase';
import { cardRowToMeta, type CardRow } from './cards';

export type LikeResult = { ok: true } | { ok: false; reason?: string };

async function call(fn: 'like_card' | 'unlike_card', cardId: string): Promise<LikeResult> {
  if (!supabase) return { ok: true }; // no backend: the heart still fills, for this tab
  const session = await ensureSession();
  if (!session) return { ok: false, reason: 'Could not start a session — try again in a moment.' };
  const { data, error } = await supabase.rpc(fn, { p_card_id: cardId });
  if (error) return { ok: false, reason: error.message || 'Could not save that.' };
  const r = (data ?? {}) as { ok?: boolean; reason?: string };
  return r.ok ? { ok: true } : { ok: false, reason: r.reason };
}

export const likeCard = (cardId: string) => call('like_card', cardId);
export const unlikeCard = (cardId: string) => call('unlike_card', cardId);

/**
 * The cards this actor liked that are still live, newest first. Empty without a backend;
 * null when the list could not be read, so the caller keeps what it has.
 */
export async function myLikes(): Promise<GameMeta[] | null> {
  if (!supabase) return [];
  const session = await ensureSession();
  if (!session) return null;
  const { data, error } = await supabase.rpc('my_likes');
  if (error || !Array.isArray(data)) return null;
  return (data as CardRow[]).map(cardRowToMeta);
}
